import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Proposicao } from '../types';
import JabutiIndicator from './JabutiIndicator';
import './ProposicaoCard.css';

interface CardProps {
  proposicao: Proposicao;
  variant?: 'v1' | 'v2' | 'v3' | 'v4';
}

const tipoColors: Record<string, { bg: string; color: string }> = {
  PEC: { bg: '#daf0ed', color: '#3d9a80' },
  PL: { bg: '#fef4a8', color: '#d9960c' },
  PLP: { bg: '#f3e5ff', color: '#7c4dff' },
  MPV: { bg: '#fdecea', color: '#c0392b' },
  PDL: { bg: '#e3f2fd', color: '#1a56a8' },
};

export default function ProposicaoCard({ proposicao, variant = 'v1' }: CardProps) {
  const navigate = useNavigate();
  const colors = tipoColors[proposicao.tipo] || { bg: '#f0f0f0', color: '#666' };

  const handleClick = () => {
    navigate(`/detail/${proposicao.id}`);
  };

  if (variant === 'v1') {
    return <CardV1 prop={proposicao} colors={colors} onClick={handleClick} />;
  }
  if (variant === 'v2') {
    return <CardV2 prop={proposicao} colors={colors} onClick={handleClick} />;
  }
  if (variant === 'v3') {
    return <CardV3 prop={proposicao} colors={colors} onClick={handleClick} />;
  }
  if (variant === 'v4') {
    return <CardV4 prop={proposicao} colors={colors} onClick={handleClick} />;
  }

  return null;
}

// V1: Linha compacta (40px)
function CardV1({
  prop,
  colors,
  onClick,
}: {
  prop: Proposicao;
  colors: any;
  onClick: () => void;
}) {
  return (
    <div className="card-v1" onClick={onClick}>
      <div className="v1-badge" style={{ backgroundColor: colors.bg, color: colors.color }}>
        {prop.tipo}
      </div>
      <div className="v1-content">
        <span className="v1-title">{prop.titulo}</span>
        <span className="v1-meta">
          {prop.numero}/{prop.ano} • {prop.autor}
        </span>
      </div>
      {(prop as any).jabuti && <JabutiIndicator jabuti={(prop as any).jabuti} compact={true} />}
      <div className="v1-status" style={{ backgroundColor: colors.bg, color: colors.color }}>
        {prop.status}
      </div>
    </div>
  );
}

// V2: Vertical (180px)
function CardV2({
  prop,
  colors,
  onClick,
}: {
  prop: Proposicao;
  colors: any;
  onClick: () => void;
}) {
  const formattedDate = new Date(prop.data_apresentacao).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  return (
    <div className="card-v2" onClick={onClick}>
      <div className="v2-header">
        <div className="v2-badge" style={{ backgroundColor: colors.bg, color: colors.color }}>
          {prop.tipo}
        </div>
        <div className="v2-numero">{prop.numero}</div>
      </div>

      <div className="v2-body">
        <h3 className="v2-title">{prop.titulo}</h3>
        <p className="v2-author">{prop.autor}</p>
        <div className="v2-meta">
          <span className="v2-date">{formattedDate}</span>
          <span
            className="v2-status-badge"
            style={{ backgroundColor: colors.bg, color: colors.color }}
          >
            {prop.status}
          </span>
        </div>
      </div>
    </div>
  );
}

// V3: Editorial (220px)
function CardV3({
  prop,
  colors,
  onClick,
}: {
  prop: Proposicao;
  colors: any;
  onClick: () => void;
}) {
  const formattedDate = new Date(prop.data_apresentacao).toLocaleDateString('pt-BR', {
    month: 'long',
    day: '2-digit',
  });

  return (
    <div className="card-v3" onClick={onClick}>
      {(prop as any).jabuti?.level !== 'low' && (
        <div onClick={(e) => e.stopPropagation()}>
          <JabutiIndicator jabuti={(prop as any).jabuti} compact={false} />
        </div>
      )}
      <div className="v3-header">
        <div className="v3-badge" style={{ backgroundColor: colors.bg, color: colors.color }}>
          {prop.tipo}
        </div>
        <span className="v3-date">{formattedDate}</span>
      </div>

      <h3 className="v3-title">{prop.titulo}</h3>

      <p className="v3-ementa">{prop.ementa || 'Sem ementa disponível'}</p>

      <div className="v3-footer">
        <div className="v3-author">De <strong>{prop.autor}</strong></div>
        <div className="v3-votacoes">
          <span className="v3-votacoes-icon">🗳️</span>
          <span>{prop.numero_votacoes} votações</span>
        </div>
      </div>
    </div>
  );
}

// V4: Dossiê denso (240px)
function CardV4({
  prop,
  colors,
  onClick,
}: {
  prop: Proposicao;
  colors: any;
  onClick: () => void;
}) {
  return (
    <div className="card-v4" onClick={onClick}>
      <div className="v4-header">
        <div className="v4-badge" style={{ backgroundColor: colors.bg, color: colors.color }}>
          {prop.tipo}
        </div>
        <span className="v4-numero">{prop.numero}/{prop.ano}</span>
      </div>

      <h3 className="v4-title">{prop.titulo}</h3>

      <div className="v4-grid">
        <div className="v4-grid-item">
          <span className="v4-label">Autor</span>
          <span className="v4-value">{prop.autor}</span>
        </div>
        <div className="v4-grid-item">
          <span className="v4-label">Status</span>
          <span className="v4-value" style={{ color: colors.color }}>
            {prop.status}
          </span>
        </div>
        <div className="v4-grid-item">
          <span className="v4-label">Votações</span>
          <span className="v4-value">{prop.numero_votacoes}</span>
        </div>
      </div>

      <div className="v4-timeline-preview">
        <div className="v4-timeline-dots">
          <div className="v4-dot active"></div>
          <div className="v4-dot"></div>
          <div className="v4-dot"></div>
        </div>
        <span className="v4-timeline-text">3 eventos de tramitação</span>
      </div>

      <div className="v4-similares-preview">
        <span className="v4-similares-label">Similares:</span>
        <span className="v4-similares-count">5 proposições relacionadas</span>
      </div>
    </div>
  );
}
